/**
 * Calculadora rápida del mes actual: dada una transacción hipotética
 * (un solo ingreso o gasto), muestra cómo cambian ISR, IVA y caja.
 *
 * Distinto de ProjectionsTab (multi-mes con recurrencias). Esta es para
 * preguntas puntuales: "¿si me pagan $50k hoy, cuánto sube el ISR?",
 * "¿si compro este equipo de $30k facturado, qué pasa?".
 */
import { useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  TrendingUp,
  TrendingDown,
  Calculator,
  ArrowRight,
  Plus,
  Trash2,
  Save,
  LayoutGrid,
  Upload,
} from "lucide-react";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import clsx from "clsx";
import { api } from "@/lib/api";
import { formatMxnCents, formatPeriod } from "@/lib/format";
import { BurnRateGauge, utilidadPctTone } from "@/features/finance/BurnRateGauge";
import { ChartCard } from "@/features/finance/ChartCard";
import {
  useScenarios,
  type Hypo,
  type Scenario,
  type SimResponse,
  type CashFlow,
} from "@/features/finance/scenarios";

const CHART_COLORS = {
  ingresos: "oklch(0.78 0.16 145)",
  gastos: "oklch(0.65 0.2 25)",
  margen: "oklch(0.62 0.18 220)",
};

interface AnalysisTx {
  kind: "ingreso" | "gasto" | "transferencia";
  subtotalCents: number;
  ivaCents: number;
  projectId: string | null;
}

interface ProjectInfo {
  id: string;
  name: string;
}

interface MarginRow {
  name: string;
  ingresosCents: number;
  gastosCents: number;
  margenCents: number;
}

function newHypo(): Hypo {
  return {
    id: crypto.randomUUID(),
    kind: "ingreso",
    subtotal: 0,
    iva: 0,
    paymentDate: new Date().toISOString().slice(0, 10),
    facturado: true,
    projectId: "",
  };
}

export function CalculatorTab({ period }: { period: string }) {
  const [hypos, setHypos] = useState<Hypo[]>(() => [newHypo()]);
  const saveDialogRef = useRef<HTMLDialogElement>(null);
  const [compareIds, setCompareIds] = useState<Set<string>>(() => new Set());
  const scenariosState = useScenarios();
  const scenariosForPeriod = useMemo(
    () => scenariosState.scenarios.filter((s) => s.period === period),
    [scenariosState.scenarios, period],
  );

  const projectsQuery = useQuery<{ projects: ProjectInfo[] }>({
    queryKey: ["projects", "calculator"],
    queryFn: () => api.get("/projects?status=active,paused,completed"),
  });

  const sim = useMutation<SimResponse>({
    mutationFn: () =>
      api.post("/finance/simulations/isr", {
        period,
        hypotheticals: hypos
          .filter((h) => h.subtotal > 0)
          .map((h) => ({
            kind: h.kind,
            subtotalCents: Math.round(h.subtotal * 100),
            ivaCents: Math.round(h.iva * 100),
            paymentDate: h.paymentDate,
            facturado: h.facturado,
          })),
      }),
  });

  const updateHypo = (id: string, patch: Partial<Hypo>) =>
    setHypos((prev) => prev.map((h) => (h.id === id ? { ...h, ...patch } : h)));
  const removeHypo = (id: string) =>
    setHypos((prev) => (prev.length > 1 ? prev.filter((h) => h.id !== id) : prev));
  const addHypo = () => setHypos((prev) => [...prev, newHypo()]);

  const anyValid = hypos.some((h) => h.subtotal > 0);

  // Selección para comparar. Solo IDs que aún existen entre los scenarios.
  const validCompareIds = useMemo(
    () => new Set([...compareIds].filter((id) => scenariosForPeriod.some((s) => s.id === id))),
    [compareIds, scenariosForPeriod],
  );

  const toggleCompare = (id: string) =>
    setCompareIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  function handleSaveScenario(name: string) {
    if (!sim.data) return;
    const created = scenariosState.add({
      name,
      period,
      hypos,
      result: sim.data,
    });
    setCompareIds((prev) => new Set([...prev, created.id]));
    saveDialogRef.current?.close();
  }

  function handleLoadScenario(scenario: Scenario) {
    // Restaurar IDs únicos para no chocar con los actuales.
    setHypos(scenario.hypos.map((h) => ({ ...h, id: crypto.randomUUID() })));
  }

  const compareScenarios = useMemo(
    () => scenariosForPeriod.filter((s) => validCompareIds.has(s.id)),
    [scenariosForPeriod, validCompareIds],
  );

  return (
    <div className="space-y-4">
      <div className="card-elevated p-5 space-y-4">
        <div>
          <h3 className="text-sm font-semibold uppercase tracking-wider text-base-content/70">
            Calculadora — {formatPeriod(period)}
          </h3>
          <p className="text-xs text-base-content/55 mt-1 leading-relaxed">
            Agrega uno o varios movimientos hipotéticos (ingresos o gastos) y ve cómo cambian ISR,
            IVA y caja del mes actual. No toca tus datos reales.
          </p>
        </div>

        <div className="space-y-3">
          {hypos.map((h, idx) => (
            <HypoRow
              key={h.id}
              index={idx}
              hypo={h}
              projects={projectsQuery.data?.projects ?? []}
              canRemove={hypos.length > 1}
              onChange={(patch) => updateHypo(h.id, patch)}
              onRemove={() => removeHypo(h.id)}
            />
          ))}
        </div>

        <div className="flex items-center justify-between pt-2 border-t border-base-300/60 gap-3">
          <button
            type="button"
            onClick={addHypo}
            disabled={hypos.length >= 20}
            className="btn btn-ghost btn-sm gap-1"
          >
            <Plus className="size-4" /> Agregar movimiento
          </button>
          <button
            type="button"
            onClick={() => sim.mutate()}
            disabled={sim.isPending || !anyValid}
            className="btn btn-primary btn-sm"
          >
            {sim.isPending && <span className="loading loading-spinner loading-xs" />}
            <Calculator className="size-4" /> Calcular impacto
          </button>
        </div>
      </div>

      {sim.data && (
        <>
          <ResultPanel data={sim.data} hypos={hypos} period={period} />
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => saveDialogRef.current?.showModal()}
              className="btn btn-secondary btn-sm gap-1"
            >
              <Save className="size-4" /> Guardar este escenario
            </button>
          </div>
        </>
      )}

      {scenariosForPeriod.length > 0 && (
        <SavedScenariosList
          scenarios={scenariosForPeriod}
          selectedIds={validCompareIds}
          onToggle={toggleCompare}
          onLoad={handleLoadScenario}
          onRename={scenariosState.rename}
          onRemove={(id) => {
            scenariosState.remove(id);
            setCompareIds((prev) => {
              const next = new Set(prev);
              next.delete(id);
              return next;
            });
          }}
        />
      )}

      {compareScenarios.length > 0 && (
        <CompareTable
          baseline={sim.data?.baseline ?? compareScenarios[0]!.result.baseline}
          scenarios={compareScenarios}
        />
      )}

      <SaveScenarioDialog
        ref={saveDialogRef}
        defaultName={`Escenario ${scenariosForPeriod.length + 1}`}
        onSave={handleSaveScenario}
        onCancel={() => saveDialogRef.current?.close()}
      />
    </div>
  );
}

// ─── Saved scenarios list ─────────────────────────────────────────────────

function SavedScenariosList({
  scenarios,
  selectedIds,
  onToggle,
  onLoad,
  onRename,
  onRemove,
}: {
  scenarios: Scenario[];
  selectedIds: Set<string>;
  onToggle: (id: string) => void;
  onLoad: (s: Scenario) => void;
  onRename: (id: string, name: string) => void;
  onRemove: (id: string) => void;
}) {
  return (
    <div className="card-elevated p-4 space-y-2">
      <header className="flex items-center justify-between">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-base-content/70 flex items-center gap-2">
          <LayoutGrid className="size-4" />
          Escenarios guardados ({scenarios.length})
        </h3>
        <span className="text-xs text-base-content/55">
          Marca para comparar — la tabla aparece abajo
        </span>
      </header>
      <ul className="divide-y divide-base-300/40">
        {scenarios.map((s) => (
          <ScenarioRow
            key={s.id}
            scenario={s}
            selected={selectedIds.has(s.id)}
            onToggle={() => onToggle(s.id)}
            onLoad={() => onLoad(s)}
            onRename={(name) => onRename(s.id, name)}
            onRemove={() => onRemove(s.id)}
          />
        ))}
      </ul>
    </div>
  );
}

function ScenarioRow({
  scenario,
  selected,
  onToggle,
  onLoad,
  onRename,
  onRemove,
}: {
  scenario: Scenario;
  selected: boolean;
  onToggle: () => void;
  onLoad: () => void;
  onRename: (name: string) => void;
  onRemove: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(scenario.name);
  const moveCount = scenario.hypos.filter((h) => h.subtotal > 0).length;
  const created = new Date(scenario.createdAt * 1000);

  function commitRename() {
    const trimmed = name.trim();
    if (trimmed && trimmed !== scenario.name) onRename(trimmed);
    else setName(scenario.name);
    setEditing(false);
  }

  return (
    <li className="py-2 flex items-center gap-3 text-sm">
      <input
        type="checkbox"
        checked={selected}
        onChange={onToggle}
        className="checkbox checkbox-sm checkbox-primary shrink-0"
        aria-label={`Comparar ${scenario.name}`}
      />
      <div className="min-w-0 flex-1">
        {editing ? (
          <input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={commitRename}
            onKeyDown={(e) => {
              if (e.key === "Enter") commitRename();
              if (e.key === "Escape") {
                setName(scenario.name);
                setEditing(false);
              }
            }}
            className="input input-bordered input-xs w-full"
          />
        ) : (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="font-medium truncate text-left hover:text-primary"
            title="Click para renombrar"
          >
            {scenario.name}
          </button>
        )}
        <div className="text-xs text-base-content/55">
          {moveCount} movimiento{moveCount === 1 ? "" : "s"} ·{" "}
          {created.toLocaleDateString("es-MX", {
            day: "2-digit",
            month: "short",
            hour: "2-digit",
            minute: "2-digit",
          })}
        </div>
      </div>
      <button
        type="button"
        onClick={onLoad}
        className="btn btn-ghost btn-xs gap-1"
        title="Cargar al formulario para editar"
      >
        <Upload className="size-3.5" /> Cargar
      </button>
      <button
        type="button"
        onClick={() => {
          if (confirm(`¿Eliminar el escenario "${scenario.name}"?`)) onRemove();
        }}
        className="btn btn-ghost btn-xs btn-square"
        aria-label="Eliminar"
      >
        <Trash2 className="size-3.5 text-error" />
      </button>
    </li>
  );
}

// ─── Save scenario dialog ─────────────────────────────────────────────────

const SaveScenarioDialog = ({
  ref,
  defaultName,
  onSave,
  onCancel,
}: {
  ref: React.RefObject<HTMLDialogElement | null>;
  defaultName: string;
  onSave: (name: string) => void;
  onCancel: () => void;
}) => {
  const [name, setName] = useState(defaultName);
  return (
    <dialog ref={ref} className="modal" onClose={onCancel}>
      <div className="modal-box card-elevated max-w-md">
        <h3 className="text-lg font-display font-semibold mb-3">Guardar escenario</h3>
        <p className="text-xs text-base-content/60 mb-3">
          Los escenarios se guardan en este navegador. Podrás compararlos en una tabla.
        </p>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            const trimmed = name.trim();
            if (trimmed) {
              onSave(trimmed);
              setName(defaultName);
            }
          }}
          className="space-y-3"
        >
          <input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Ej. Ingreso de Tlacu en mayo"
            className="input input-bordered w-full"
          />
          <div className="flex justify-end gap-2 pt-2 border-t border-base-300/60">
            <button type="button" onClick={onCancel} className="btn btn-ghost btn-sm">
              Cancelar
            </button>
            <button type="submit" className="btn btn-primary btn-sm" disabled={!name.trim()}>
              Guardar
            </button>
          </div>
        </form>
      </div>
      <form method="dialog" className="modal-backdrop">
        <button>cerrar</button>
      </form>
    </dialog>
  );
};

// ─── Compare table ────────────────────────────────────────────────────────

type BetterWhen = "higher" | "lower" | "none";
type Unit = "money" | "pct";

interface CompareRowDef {
  label: string;
  sublabel?: string;
  get: (cf: CashFlow) => number;
  unit: Unit;
  /** Indica si subir vs Baseline es bueno (success) o malo (error). */
  betterWhen: BetterWhen;
  /** Si true, la celda del valor se pinta con tone según signo. */
  signColored?: boolean;
  group?: "totales" | "impuestos" | "resultado";
}

const COMPARE_ROWS: CompareRowDef[] = [
  {
    label: "Ingresos cobrados",
    sublabel: "con IVA",
    get: (cf) => cf.ingresosCobradosCents + cf.ivaTrasladadoCents,
    unit: "money",
    betterWhen: "higher",
    group: "totales",
  },
  {
    label: "Gastos pagados",
    sublabel: "con IVA",
    get: (cf) => cf.gastosPagadosCents + cf.ivaAcreditableCents,
    unit: "money",
    betterWhen: "lower",
    group: "totales",
  },
  {
    label: "Utilidad bruta",
    sublabel: "ingresos − gastos",
    get: (cf) => cf.ingresosCobradosCents - cf.gastosPagadosCents,
    unit: "money",
    betterWhen: "higher",
    signColored: true,
    group: "totales",
  },
  {
    label: "ISR a pagar",
    sublabel: "30% sobre utilidad bruta",
    get: (cf) => cf.isrCents,
    unit: "money",
    betterWhen: "lower",
    group: "impuestos",
  },
  {
    label: "IVA saldo",
    sublabel: "trasladado − acreditable",
    get: (cf) => cf.ivaSaldoCents,
    unit: "money",
    betterWhen: "lower",
    group: "impuestos",
  },
  {
    label: "Utilidad neta",
    sublabel: "bruta − ISR − IVA",
    get: (cf) => {
      const margen = cf.ingresosCobradosCents - cf.gastosPagadosCents;
      return margen - cf.isrCents - cf.ivaSaldoCents;
    },
    unit: "money",
    betterWhen: "higher",
    signColored: true,
    group: "resultado",
  },
  {
    label: "Burn rate",
    sublabel: "gastos / ingresos",
    get: (cf) =>
      cf.ingresosCobradosCents > 0 ? (cf.gastosPagadosCents / cf.ingresosCobradosCents) * 100 : 0,
    unit: "pct",
    betterWhen: "none", // tiene tono propio (semáforo)
    group: "resultado",
  },
];

function CompareTable({ baseline, scenarios }: { baseline: CashFlow; scenarios: Scenario[] }) {
  return (
    <div className="card-elevated p-4">
      <h3 className="text-sm font-semibold uppercase tracking-wider text-base-content/70 mb-3">
        Comparativo · Baseline vs {scenarios.length} escenario
        {scenarios.length === 1 ? "" : "s"}
      </h3>
      <div className="overflow-x-auto">
        <table className="table table-sm">
          <thead>
            <tr>
              <th></th>
              <th className="text-right text-base-content/65">Baseline</th>
              {scenarios.map((s) => (
                <th key={s.id} className="text-right">
                  {s.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {COMPARE_ROWS.map((row, i) => {
              const baseV = row.get(baseline);
              const isLastInGroup =
                i < COMPARE_ROWS.length - 1 && COMPARE_ROWS[i + 1]!.group !== row.group;
              return (
                <tr
                  key={row.label}
                  className={`${row.signColored ? "font-semibold" : ""} ${isLastInGroup ? "[&>td]:border-b-2 [&>th]:border-b-2 [&>td]:border-base-300/60" : ""}`}
                >
                  <td>
                    <div className={row.signColored ? "" : "text-base-content/75"}>{row.label}</div>
                    {row.sublabel && (
                      <div className="text-[10px] text-base-content/45 font-normal">
                        {row.sublabel}
                      </div>
                    )}
                  </td>
                  <td className="text-right">
                    <CompareCell value={baseV} unit={row.unit} signColored={row.signColored} />
                  </td>
                  {scenarios.map((s) => {
                    const v = row.get(s.result.withHypothetical);
                    return (
                      <td key={s.id} className="text-right">
                        <CompareCell
                          value={v}
                          baseline={baseV}
                          unit={row.unit}
                          betterWhen={row.betterWhen}
                          signColored={row.signColored}
                        />
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CompareCell({
  value,
  baseline,
  unit,
  betterWhen,
  signColored,
}: {
  value: number;
  /** Si se pasa, se muestra el delta vs este valor. Si no, es la celda Baseline. */
  baseline?: number;
  unit: Unit;
  betterWhen?: BetterWhen;
  /** Si true, el monto se colorea según signo (success ≥ 0, error < 0). */
  signColored?: boolean;
}) {
  const valueText =
    unit === "money" ? formatMxnCents(value) : value === 0 ? "—" : `${value.toFixed(1)}%`;

  // Color del monto.
  let valueClass = "text-base-content/85";
  if (signColored) valueClass = value >= 0 ? "text-success" : "text-error";
  if (unit === "pct" && value !== 0) {
    // Burn rate: usar zonas del semáforo.
    if (value < 55 || value > 90) valueClass = "text-error";
    else if (value < 70 || value > 80) valueClass = "text-warning";
    else valueClass = "text-success";
  }

  // Para Baseline (sin delta): solo el valor.
  if (baseline === undefined) {
    return <div className={`font-mono tabular-nums ${valueClass}`}>{valueText}</div>;
  }

  // Delta.
  const diff = value - baseline;
  const hasDelta = Math.abs(diff) >= (unit === "pct" ? 0.05 : 1);
  let deltaClass = "text-base-content/50";
  let arrow = "=";
  if (hasDelta) {
    if (betterWhen === "higher") {
      deltaClass = diff > 0 ? "text-success" : "text-error";
      arrow = diff > 0 ? "↑" : "↓";
    } else if (betterWhen === "lower") {
      deltaClass = diff < 0 ? "text-success" : "text-error";
      arrow = diff > 0 ? "↑" : "↓";
    } else {
      // "none" — informativo, sin juicio (burn rate ya se colorea por zona).
      deltaClass = "text-base-content/65";
      arrow = diff > 0 ? "↑" : "↓";
    }
  }

  const pctText =
    unit === "money" && baseline !== 0
      ? ` · ${diff > 0 ? "+" : ""}${((diff / Math.abs(baseline)) * 100).toFixed(1)}%`
      : unit === "pct" && hasDelta
        ? ` · ${diff > 0 ? "+" : ""}${diff.toFixed(1)}pts`
        : "";

  const diffText =
    unit === "money"
      ? `${diff > 0 ? "+" : ""}${formatMxnCents(diff)}`
      : unit === "pct"
        ? hasDelta
          ? `${diff > 0 ? "+" : ""}${diff.toFixed(1)}pts`
          : "sin cambio"
        : "";

  return (
    <div>
      <div className={`font-mono tabular-nums ${valueClass}`}>{valueText}</div>
      {hasDelta ? (
        <div className={`text-[10px] tabular-nums ${deltaClass}`}>
          {arrow} {unit === "money" ? `${diffText}${pctText}` : diffText}
        </div>
      ) : (
        <div className="text-[10px] text-base-content/40">= sin cambio</div>
      )}
    </div>
  );
}

function HypoRow({
  index,
  hypo,
  projects,
  canRemove,
  onChange,
  onRemove,
}: {
  index: number;
  hypo: Hypo;
  projects: ProjectInfo[];
  canRemove: boolean;
  onChange: (patch: Partial<Hypo>) => void;
  onRemove: () => void;
}) {
  const calcIva16 = () => onChange({ iva: Math.round(hypo.subtotal * 0.16 * 100) / 100 });
  return (
    <div className="rounded-lg border border-base-300/60 p-3 space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wider text-base-content/55">
          Movimiento {index + 1}
        </span>
        {canRemove && (
          <button
            type="button"
            onClick={onRemove}
            className="btn btn-ghost btn-xs btn-square"
            aria-label="Quitar movimiento"
          >
            <Trash2 className="size-3.5 text-error" />
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <Field label="Tipo">
          <select
            className="select select-bordered select-sm w-full"
            value={hypo.kind}
            onChange={(e) => onChange({ kind: e.target.value as "ingreso" | "gasto" })}
          >
            <option value="ingreso">Ingreso (cobrado)</option>
            <option value="gasto">Gasto (pagado)</option>
          </select>
        </Field>
        <Field label="Subtotal (MXN)">
          <input
            type="number"
            step="0.01"
            className="input input-bordered input-sm w-full"
            placeholder="0.00"
            value={hypo.subtotal || ""}
            onChange={(e) => onChange({ subtotal: Number(e.target.value) })}
          />
        </Field>
        <Field
          label="IVA (MXN)"
          help={
            <button type="button" className="link link-primary text-xs" onClick={calcIva16}>
              Calcular 16%
            </button>
          }
        >
          <input
            type="number"
            step="0.01"
            className="input input-bordered input-sm w-full"
            placeholder="0.00"
            value={hypo.iva || ""}
            onChange={(e) => onChange({ iva: Number(e.target.value) })}
          />
        </Field>
        <Field label="Fecha (cobro / pago)">
          <input
            type="date"
            className="input input-bordered input-sm w-full"
            value={hypo.paymentDate}
            onChange={(e) => onChange({ paymentDate: e.target.value })}
          />
        </Field>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Proyecto (opcional)">
          <select
            className="select select-bordered select-sm w-full"
            value={hypo.projectId}
            onChange={(e) => onChange({ projectId: e.target.value })}
          >
            <option value="">— Sin proyecto (Axorial general) —</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </Field>
        {hypo.kind === "gasto" && (
          <label className="cursor-pointer label justify-start gap-3 self-end pb-2">
            <input
              type="checkbox"
              checked={hypo.facturado}
              onChange={(e) => onChange({ facturado: e.target.checked })}
              className="checkbox checkbox-primary checkbox-sm"
            />
            <span className="text-sm">Tiene CFDI (cuenta para IVA acreditable)</span>
          </label>
        )}
      </div>
    </div>
  );
}

function ResultPanel({
  data,
  hypos,
  period,
}: {
  data: SimResponse;
  /** Movimientos hipotéticos del cliente. Solo los con subtotal > 0 se enviaron. */
  hypos: Hypo[];
  period: string;
}) {
  // Solo los movimientos válidos que el cliente envió + el backend aceptó
  // (los fuera de período están en data.droppedOutsidePeriod).
  const activeHypos = useMemo(() => hypos.filter((h) => h.subtotal > 0), [hypos]);
  const dropped = data.droppedOutsidePeriod ?? [];

  // Margen por proyecto: traemos las txs reales del mes (igual que AnalysisTab)
  // y armamos dos series — "hoy" (baseline) y "con hipotético" (sumamos cada
  // movimiento al bucket del projectId que el usuario eligió por fila).
  const fromDate = `${period}-01`;
  const toDate = useMemo(() => {
    const [y, m] = period.split("-").map(Number);
    const last = new Date(y!, m!, 0).getDate();
    return `${period}-${String(last).padStart(2, "0")}`;
  }, [period]);

  const txsQ = useQuery<{ transactions: AnalysisTx[] }>({
    queryKey: ["finance", "calculator-margin", fromDate, toDate],
    queryFn: () => api.get(`/finance/transactions?from=${fromDate}&to=${toDate}&affectsTotals=1`),
  });
  const projectsQ = useQuery<{ projects: ProjectInfo[] }>({
    queryKey: ["projects", "calculator-margin"],
    queryFn: () => api.get("/projects?status=active,paused,completed"),
  });

  const { marginBefore, marginAfter, highlightNames } = useMemo(() => {
    if (!txsQ.data) {
      return {
        marginBefore: [] as MarginRow[],
        marginAfter: [] as MarginRow[],
        highlightNames: new Set<string>(),
      };
    }
    const byId = new Map((projectsQ.data?.projects ?? []).map((p) => [p.id, p]));

    // Pasada 1: agregar el baseline desde txs reales.
    const map = new Map<string, { name: string; ingresos: number; gastos: number }>();
    const ungrouped = { name: "Axorial (general)", ingresos: 0, gastos: 0 };
    for (const t of txsQ.data.transactions) {
      const proj = t.projectId ? byId.get(t.projectId) : null;
      const bucket = proj
        ? (map.get(proj.id) ??
          (() => {
            const entry = { name: proj.name, ingresos: 0, gastos: 0 };
            map.set(proj.id, entry);
            return entry;
          })())
        : ungrouped;
      if (t.kind === "ingreso") bucket.ingresos += t.subtotalCents;
      else if (t.kind === "gasto") bucket.gastos += t.subtotalCents;
    }

    const buildRows = (
      m: Map<string, { name: string; ingresos: number; gastos: number }>,
      u: { name: string; ingresos: number; gastos: number },
    ) => {
      const arr = [...m.values()];
      if (u.ingresos > 0 || u.gastos > 0) arr.push(u);
      return arr
        .map((b) => ({
          name: b.name,
          ingresosCents: b.ingresos,
          gastosCents: b.gastos,
          margenCents: b.ingresos - b.gastos,
        }))
        .toSorted((a, b) => b.margenCents - a.margenCents);
    };
    const before = buildRows(map, ungrouped);

    // Pasada 2: clonar baseline y aplicar cada hipotético activo al bucket
    // de su projectId. Saltamos los droppedOutsidePeriod (no afectaron caja).
    const map2 = new Map<string, { name: string; ingresos: number; gastos: number }>(
      [...map.entries()].map(([id, v]) => [id, { ...v }]),
    );
    const ung2 = { ...ungrouped };
    const targetNames = new Set<string>();

    for (let i = 0; i < activeHypos.length; i++) {
      if (dropped.includes(i)) continue;
      const h = activeHypos[i]!;
      const cents = Math.round(h.subtotal * 100);
      let target: { name: string; ingresos: number; gastos: number };
      if (h.projectId === "") {
        target = ung2;
      } else {
        const proj = byId.get(h.projectId);
        const name = proj?.name ?? h.projectId;
        const existing = map2.get(h.projectId);
        if (existing) {
          target = existing;
        } else {
          target = { name, ingresos: 0, gastos: 0 };
          map2.set(h.projectId, target);
        }
      }
      if (h.kind === "ingreso") target.ingresos += cents;
      else target.gastos += cents;
      targetNames.add(target.name);
    }
    const after = buildRows(map2, ung2);
    return { marginBefore: before, marginAfter: after, highlightNames: targetNames };
  }, [txsQ.data, projectsQ.data, activeHypos, dropped]);

  // Si todos los movimientos cayeron fuera del período, no hay nada que
  // comparar — mostramos solo la alerta y salimos.
  const allDropped = activeHypos.length > 0 && dropped.length === activeHypos.length;
  if (allDropped) {
    return (
      <div className="alert alert-warning text-sm">
        Ningún movimiento hipotético cae dentro de {formatPeriod(period)} — no se aplicó al cálculo.
      </div>
    );
  }

  // KPIs derivados del baseline + withHypothetical. Mantenemos la convención
  // de AnalysisTab: el monto grande es NETO (sin IVA); IVA va aparte.
  const baseline = data.baseline;
  const after = data.withHypothetical;
  const ivaNetoBaseline = baseline.ivaTrasladadoCents - baseline.ivaAcreditableCents;
  const ivaNetoAfter = after.ivaTrasladadoCents - after.ivaAcreditableCents;
  const margenBaseline = baseline.ingresosCobradosCents - baseline.gastosPagadosCents;
  const margenAfter = after.ingresosCobradosCents - after.gastosPagadosCents;
  // Utilidad neta = margen − ISR − IVA saldo. Usamos ivaSaldoCents
  // (que ya viene del endpoint) para que respete trasladado − acreditable.
  // Saldo positivo resta caja (a pagar), negativo suma (a favor / acreditable
  // a futuro). Es la línea conservadora "qué de verdad queda en mes corriente".
  const netoBaseline = margenBaseline - baseline.isrCents - baseline.ivaSaldoCents;
  const netoAfter = margenAfter - after.isrCents - after.ivaSaldoCents;

  // Resumen textual de los movimientos aplicados: "+ $X · −$Y · 2 movimientos"
  const appliedCount = activeHypos.length - dropped.length;
  const totalIngresosCents = activeHypos.reduce(
    (acc, h, i) =>
      acc +
      (dropped.includes(i) || h.kind !== "ingreso" ? 0 : Math.round((h.subtotal + h.iva) * 100)),
    0,
  );
  const totalGastosCents = activeHypos.reduce(
    (acc, h, i) =>
      acc +
      (dropped.includes(i) || h.kind !== "gasto"
        ? 0
        : Math.round((h.subtotal + (h.facturado ? h.iva : h.iva)) * 100)),
    0,
  );
  const appliedSummary =
    appliedCount === 0
      ? "Ningún movimiento aplicado"
      : `${appliedCount} movimiento${appliedCount > 1 ? "s" : ""}` +
        (totalIngresosCents > 0 ? ` · + ${formatMxnCents(totalIngresosCents)}` : "") +
        (totalGastosCents > 0 ? ` · − ${formatMxnCents(totalGastosCents)}` : "");

  return (
    <div className="space-y-3">
      {dropped.length > 0 && (
        <div className="alert alert-warning text-sm">
          {dropped.length} movimiento{dropped.length > 1 ? "s" : ""} cae
          {dropped.length > 1 ? "n" : ""} fuera de {formatPeriod(period)} y no se aplicó
          {dropped.length > 1 ? "" : ""}: revisa las fechas marcadas (
          {dropped.map((i) => `#${i + 1}`).join(", ")}).
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <DeltaCard
          label="Δ ISR a pagar"
          value={data.delta.isrCents}
          tone={data.delta.isrCents <= 0 ? "success" : "warning"}
          hint={data.delta.isrCents > 0 ? "Sube el impuesto" : "Baja o sin cambio"}
        />
        <DeltaCard
          label="Δ IVA saldo"
          value={data.delta.ivaSaldoCents}
          tone={data.delta.ivaSaldoCents <= 0 ? "success" : "warning"}
          hint={data.delta.ivaSaldoCents > 0 ? "Más a pagar al SAT" : "Más a favor"}
        />
        <DeltaCard
          label="Δ Caja neta"
          value={data.delta.netCashCents}
          tone={data.delta.netCashCents >= 0 ? "success" : "error"}
          hint={appliedSummary}
        />
      </div>

      {/* KPI tiles antes/después: visualiza cómo se mueve el mes en términos
          absolutos, no solo el delta. Utilidad bruta es operativo (pre-impuestos);
          Utilidad neta es lo que de verdad queda después de ISR + IVA. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 2xl:grid-cols-6 gap-3">
        <DualKpiTile
          label="Ingresos"
          beforeCents={baseline.ingresosCobradosCents + baseline.ivaTrasladadoCents}
          afterCents={after.ingresosCobradosCents + after.ivaTrasladadoCents}
          ivaCents={after.ivaTrasladadoCents}
          tone="success"
          icon={TrendingUp}
        />
        <DualKpiTile
          label="Gastos"
          beforeCents={baseline.gastosPagadosCents + baseline.ivaAcreditableCents}
          afterCents={after.gastosPagadosCents + after.ivaAcreditableCents}
          ivaCents={after.ivaAcreditableCents}
          tone="error"
          icon={TrendingDown}
        />
        <DualKpiTile
          label="Utilidad bruta"
          beforeCents={margenBaseline}
          afterCents={margenAfter}
          tone={margenAfter >= 0 ? "success" : "error"}
          pctLabel={
            after.ingresosCobradosCents > 0
              ? `${((margenAfter / after.ingresosCobradosCents) * 100).toFixed(1)}%`
              : undefined
          }
          pctTone={
            after.ingresosCobradosCents > 0
              ? utilidadPctTone((margenAfter / after.ingresosCobradosCents) * 100)
              : undefined
          }
          hint="ingresos − gastos"
        />
        <DualKpiTile
          label="ISR a pagar"
          beforeCents={baseline.isrCents}
          afterCents={after.isrCents}
          tone="warning"
          hint="30% sobre base"
        />
        <DualKpiTile
          label="IVA neto"
          beforeCents={ivaNetoBaseline}
          afterCents={ivaNetoAfter}
          tone={ivaNetoAfter > 0 ? "warning" : "success"}
          hint={ivaNetoAfter > 0 ? "a pagar" : ivaNetoAfter < 0 ? "a favor" : "—"}
        />
        <DualKpiTile
          label="Utilidad neta"
          beforeCents={netoBaseline}
          afterCents={netoAfter}
          tone={netoAfter >= 0 ? "success" : "error"}
          pctLabel={
            after.ingresosCobradosCents > 0
              ? `${((netoAfter / after.ingresosCobradosCents) * 100).toFixed(1)}%`
              : undefined
          }
          pctTone={
            after.ingresosCobradosCents > 0
              ? utilidadPctTone((netoAfter / after.ingresosCobradosCents) * 100)
              : undefined
          }
          hint="bruta − ISR − IVA"
        />
      </div>

      {/* Salud financiera: dos gauges juxtapuestos para comparar visualmente
          el burn rate antes y después del hipotético. */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <ChartCard title="Salud financiera — hoy" subtitle="Burn rate (gastos / ingresos)">
          <BurnRateGauge
            ingresosCents={baseline.ingresosCobradosCents}
            gastosCents={baseline.gastosPagadosCents}
            isrCents={baseline.isrCents}
            ivaSaldoCents={baseline.ivaSaldoCents}
          />
        </ChartCard>
        <ChartCard
          title="Salud financiera — con hipotético"
          subtitle="Burn rate (gastos / ingresos)"
        >
          <BurnRateGauge
            ingresosCents={after.ingresosCobradosCents}
            gastosCents={after.gastosPagadosCents}
            isrCents={after.isrCents}
            ivaSaldoCents={after.ivaSaldoCents}
          />
        </ChartCard>
      </div>

      {/* Margen por proyecto: dos paneles lado a lado para comparar el rango
          actual con el escenario hipotético aplicado al proyecto elegido. */}
      {marginBefore.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <ChartCard
            title="Utilidad bruta por proyecto — hoy"
            subtitle={`Ingresos − gastos del mes (${formatPeriod(period)})`}
          >
            <MarginByProjectChart data={marginBefore} />
          </ChartCard>
          <ChartCard
            title="Utilidad bruta por proyecto — con hipotético"
            subtitle={
              highlightNames.size > 0
                ? `Aplicado a ${[...highlightNames].join(", ")}`
                : "Sin movimientos aplicados"
            }
          >
            <MarginByProjectChart data={marginAfter} highlight={highlightNames} />
          </ChartCard>
        </div>
      )}

      <div className="card-elevated p-5">
        <h4 className="text-sm font-semibold uppercase tracking-wider text-base-content/70 mb-3">
          Antes vs después
        </h4>
        <div className="overflow-x-auto">
          <table className="table table-sm">
            <thead>
              <tr>
                <th></th>
                <th className="text-right">Hoy</th>
                <th className="text-right">Con hipotético</th>
                <th className="text-right">Δ</th>
              </tr>
            </thead>
            <tbody>
              <CompareRow
                label="Ingresos cobrados"
                a={data.baseline.ingresosCobradosCents}
                b={data.withHypothetical.ingresosCobradosCents}
              />
              <CompareRow
                label="Gastos pagados"
                a={data.baseline.gastosPagadosCents}
                b={data.withHypothetical.gastosPagadosCents}
              />
              <CompareRow
                label="Base ISR"
                a={data.baseline.baseIsrCents}
                b={data.withHypothetical.baseIsrCents}
              />
              <CompareRow
                label="ISR (30%)"
                a={data.baseline.isrCents}
                b={data.withHypothetical.isrCents}
                bold
              />
              <CompareRow
                label="IVA trasladado"
                a={data.baseline.ivaTrasladadoCents}
                b={data.withHypothetical.ivaTrasladadoCents}
              />
              <CompareRow
                label="IVA acreditable"
                a={data.baseline.ivaAcreditableCents}
                b={data.withHypothetical.ivaAcreditableCents}
              />
              <CompareRow
                label="IVA saldo"
                a={data.baseline.ivaSaldoCents}
                b={data.withHypothetical.ivaSaldoCents}
                bold
              />
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function CompareRow({
  label,
  a,
  b,
  bold,
}: {
  label: string;
  a: number;
  b: number;
  bold?: boolean;
}) {
  const delta = b - a;
  return (
    <tr className={bold ? "font-semibold" : ""}>
      <td>{label}</td>
      <td className="text-right font-mono tabular-nums">{formatMxnCents(a)}</td>
      <td className="text-right font-mono tabular-nums">{formatMxnCents(b)}</td>
      <td
        className={clsx(
          "text-right font-mono tabular-nums",
          delta > 0 ? "text-warning" : delta < 0 ? "text-success" : "text-base-content/45",
        )}
      >
        {delta > 0 ? "+" : ""}
        {formatMxnCents(delta)}
      </td>
    </tr>
  );
}

function DeltaCard({
  label,
  value,
  tone,
  hint,
}: {
  label: string;
  value: number;
  tone: "success" | "error" | "warning";
  hint?: string;
}) {
  const colors = {
    success: "text-success",
    error: "text-error",
    warning: "text-warning",
  };
  const Icon = value >= 0 ? TrendingUp : TrendingDown;
  return (
    <div className="card-elevated p-5">
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold uppercase tracking-wider text-base-content/60">
          {label}
        </div>
        <Icon className={clsx("size-4", colors[tone])} />
      </div>
      <div className={clsx("mt-2 text-2xl font-display font-bold tabular-nums", colors[tone])}>
        {value >= 0 ? "+" : ""}
        {formatMxnCents(value)}
      </div>
      {hint && <div className="text-xs text-base-content/55 mt-1">{hint}</div>}
    </div>
  );
}

function MarginByProjectChart({
  data,
  highlight,
}: {
  data: MarginRow[];
  /** Si se incluye, resalta visualmente los buckets cuyos nombres están en el set (bold). */
  highlight?: Set<string>;
}) {
  return (
    <ResponsiveContainer width="100%" height={Math.max(220, data.length * 32)}>
      <ComposedChart
        data={data}
        layout="vertical"
        margin={{ top: 8, right: 16, bottom: 8, left: 8 }}
      >
        <CartesianGrid strokeDasharray="3 3" stroke="oklch(0.85 0.01 280)" />
        <XAxis type="number" fontSize={11} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
        <YAxis
          dataKey="name"
          type="category"
          fontSize={11}
          width={140}
          tick={
            ((props: { x: number; y: number; payload: { value: string } }) => {
              const isHl = highlight?.has(props.payload.value) ?? false;
              return (
                <text
                  x={props.x}
                  y={props.y}
                  dy={4}
                  textAnchor="end"
                  fontSize={11}
                  fontWeight={isHl ? 700 : 400}
                  fill={isHl ? "oklch(0.62 0.18 220)" : "currentColor"}
                >
                  {props.payload.value}
                </text>
              );
            }) as unknown as React.ComponentProps<typeof YAxis>["tick"]
          }
        />
        <Tooltip formatter={(v) => (typeof v === "number" ? formatMxnCents(v) : String(v))} />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        <Bar dataKey="ingresosCents" name="Ingresos" fill={CHART_COLORS.ingresos} />
        <Bar dataKey="gastosCents" name="Gastos" fill={CHART_COLORS.gastos} />
        <Line
          dataKey="margenCents"
          name="Utilidad bruta"
          type="monotone"
          stroke={CHART_COLORS.margen}
          strokeWidth={2}
          dot={{ r: 3 }}
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
}

function DualKpiTile({
  label,
  beforeCents,
  afterCents,
  ivaCents,
  tone,
  icon: Icon,
  hint,
  pctLabel,
  pctTone,
}: {
  label: string;
  beforeCents: number;
  afterCents: number;
  /** Si se incluye, afterCents se interpreta como BRUTO (con IVA) y se muestra `Subtotal $X · IVA $Y` debajo. */
  ivaCents?: number;
  tone: "success" | "error" | "warning" | "neutral";
  icon?: typeof TrendingUp;
  hint?: string;
  /** Texto opcional (típicamente un %) grande a la derecha del valor. */
  pctLabel?: string;
  /** Color independiente del `pctLabel` (semáforo). Default = `tone`. */
  pctTone?: "success" | "error" | "warning" | "neutral";
}) {
  const colorClass =
    tone === "success"
      ? "text-success"
      : tone === "error"
        ? "text-error"
        : tone === "warning"
          ? "text-warning"
          : "text-base-content";
  const changed = beforeCents !== afterCents;
  const hasIva = ivaCents !== undefined && ivaCents > 0;
  return (
    <div className="card-elevated p-4">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-base-content/55 font-semibold">
        {Icon && <Icon className={clsx("size-3.5", colorClass)} />}
        {label}
      </div>
      <div className="mt-1 flex items-baseline justify-between gap-2">
        <div className={clsx("font-display font-bold text-2xl tabular-nums", colorClass)}>
          {formatMxnCents(afterCents)}
        </div>
        {pctLabel && (
          <div
            className={clsx(
              "font-display font-bold text-2xl tabular-nums shrink-0",
              pctTone
                ? pctTone === "success"
                  ? "text-success"
                  : pctTone === "error"
                    ? "text-error"
                    : pctTone === "warning"
                      ? "text-warning"
                      : "text-base-content"
                : colorClass,
            )}
          >
            {pctLabel}
          </div>
        )}
      </div>
      {changed && (
        <div className="text-[11px] text-base-content/50 mt-0.5 tabular-nums flex items-center gap-1">
          <span>{formatMxnCents(beforeCents)}</span>
          <ArrowRight className="size-3" />
          <span className="text-base-content/70">hoy → con hipotético</span>
        </div>
      )}
      {hasIva && (
        <div className="text-[11px] text-base-content/50 mt-0.5 tabular-nums">
          Subtotal {formatMxnCents(afterCents - ivaCents)} · IVA {formatMxnCents(ivaCents)}
        </div>
      )}
      {hint && !hasIva && <div className="text-[11px] text-base-content/50 mt-0.5">{hint}</div>}
    </div>
  );
}

function Field({
  label,
  help,
  children,
}: {
  label: string;
  help?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <label className="form-control w-full block">
      <div className="label py-1">
        <span className="label-text text-sm font-medium">{label}</span>
      </div>
      {children}
      {help && <p className="text-xs text-base-content/55 mt-1">{help}</p>}
    </label>
  );
}
